import { isScalar, isSeq, parseDocument } from "yaml";
import type { Document } from "yaml";
import type { JsonObject } from "@/api";
import { getAtPath, isInterpolationString } from "./recipeCoerce";
import { parseOverrideKey } from "./recipeOverrides";
import type { ParsedRecipe, RecipeEdit, RecipeFamily } from "./recipeTypes";

// eemeli/yaml 래퍼. config 본문 파싱과, 편집을 전체 본문에 round-trip 하는 책임을 소유한다.
// round-trip 은 Document 를 직접 편집(setIn)해서 주석·빈 줄·바뀌지 않은 인터폴레이션 노드를 남긴다.
// toJS()+dump 로는 절대 다시 쓰지 않는다(주석·앵커가 사라진다).
// 재직렬화는 본문의 표기를 바꾼다. 주석의 들여쓰기와 줄 위치, flow 리스트의 안쪽 공백, 줄 끝 주석의
// 열, non-canonical 숫자 표기(1e-08 → 1e-8)가 바뀐다. 값은 같다. 그래서 여기 결과는 표시와 비교에만
// 쓴다. 사용자가 편집할 본문은 원문을 보존하는 `recipeYamlPatch` 가 만든다.

// 본문을 파싱해 JS 스냅샷·계열·에러를 반환한다. 치명적 파싱 오류면 data={} · family=null 로 폴백한다.
export function parseRecipe(body: string): ParsedRecipe {
  const doc = parseDocument(body);
  const errors = doc.errors.map((error) => error.message);
  if (errors.length > 0) {
    return { data: {}, family: null, errors };
  }
  const data = toJsonObject(doc);
  return { data, family: detectFamily(data), errors: [] };
}

// 파싱된 데이터에서 계열을 감지한다. core_module.type 을 먼저 쓰고, 없으면 policy_type 으로 폴백한다.
export function detectFamily(data: JsonObject): RecipeFamily | null {
  const coreType = getAtPath(data, "policy.core_module.type");
  if (coreType === "ActionTransformer") {
    return "act";
  }
  if (coreType === "DiffusionActionTransformer") {
    return "diffusion";
  }
  const policyType = getAtPath(data, "policy.policy_type");
  if (policyType === "DirectPolicy") {
    return "act";
  }
  if (policyType === "DiffusionPolicy") {
    return "diffusion";
  }
  return null;
}

export type YamlPathKey = string | number;

// 편집을 전체 본문에 반영한 YAML 문자열. 인터폴레이션 노드·스칼라 부모를 덮어쓰는 편집은 건너뛴다.
// 치명적 파싱 오류가 있는 본문에서는 편집을 알리지 않고 버리지 않는다. 원본을 그대로 반환한다.
export function applyEditsToYaml(body: string, edits: readonly RecipeEdit[]): string {
  const doc = parseDocument(body);
  if (doc.errors.length > 0) {
    return body;
  }
  for (const edit of edits) {
    const keys = edit.path.split(".");
    if (!canSetPath(doc, keys)) {
      continue;
    }
    if (isInterpolationString(doc.getIn(keys))) {
      continue;
    }
    doc.setIn(keys, buildNode(doc, keys, edit.value));
  }
  return doc.toString();
}

// 편집 노드를 만든다. 기존 노드가 시퀀스면 flow(한 줄/블록) 스타일을 맞추고, 새 리스트는 flow 로 둔다.
function buildNode(
  doc: Document,
  keys: readonly YamlPathKey[],
  value: RecipeEdit["value"],
): unknown {
  const node = doc.createNode(value);
  if (isSeq(node)) {
    const existing = doc.getIn(keys, true);
    node.flow = isSeq(existing) ? existing.flow : true;
  }
  return node;
}

// setIn 이 스칼라/시퀀스 부모를 덮어쓰지 못하게 막는다. 존재하는 모든 상위 prefix 는 맵이어야 하고,
// 없는 prefix 는 허용한다(setIn 이 맵으로 만든다). 스칼라(인터폴레이션 포함)·시퀀스 부모면 false.
export function canSetPath(doc: Document, keys: readonly YamlPathKey[]): boolean {
  for (let index = 1; index < keys.length; index += 1) {
    const node = doc.getIn(keys.slice(0, index), true);
    if (node === undefined) {
      return true;
    }
    if (isScalar(node) || isSeq(node)) {
      return false;
    }
  }
  return true;
}

export function overridePathTokens(path: string): YamlPathKey[] {
  return path
    .split(".")
    .map((segment) => (/^\d+$/.test(segment) ? Number.parseInt(segment, 10) : segment));
}

// override dict(접두사 키 포함)를 본문에 반영한 YAML. 확정 스텝의 "원본 대비 변경" diff 시각화용이다.
// set/+/++ 는 값을 쓰고 ~ 는 경로를 지운다. BE(hydra)가 적용할 결과의 근사치라 인터폴레이션 값도
// 덮어쓴다(applyEditsToYaml 과 달리 편집 round-trip 보호가 목적이 아니다). 파싱할 수 없으면 원본을 유지한다.
export function applyOverridesToYaml(body: string, overrides: JsonObject): string {
  const doc = parseDocument(body);
  if (doc.errors.length > 0) {
    return body;
  }
  for (const [key, value] of Object.entries(overrides)) {
    const { op, path } = parseOverrideKey(key);
    const keys = overridePathTokens(path);
    if (op === "delete") {
      // `deleteIn` 은 중간 경로가 없으면 throw 한다. 이 함수는 렌더 중에 불린다. 그래서 없는 경로는
      // 건너뛴다.
      if (doc.hasIn(keys)) {
        doc.deleteIn(keys);
      }
      continue;
    }
    if (!canSetPath(doc, keys)) {
      continue;
    }
    doc.setIn(keys, buildNode(doc, keys, value as RecipeEdit["value"]));
  }
  // 내용이 없는 문서(빈 본문, 주석만 있는 본문)는 `null` 로 직렬화된다. 쓴 값이 없으면 본문을 그대로
  // 돌려준다. 그래야 받지 못한 본문이 diff 와 복사 버튼에 `null` 로 나가지 않는다.
  if (doc.contents === null) {
    return body;
  }
  return doc.toString();
}

// 같은 파서로 parse→재직렬화한 정규형. diff 의 양쪽 끝에 적용해 코스메틱 차이(지수표기 정규화·
// 80자 줄 접기·따옴표 스타일)를 없앤다. 의미는 바꾸지 않는다. 파싱 불가·빈 본문은 그대로 둔다.
export function normalizeYaml(body: string): string {
  if (body.trim() === "") {
    return body;
  }
  const doc = parseDocument(body);
  if (doc.errors.length > 0) {
    return body;
  }
  return doc.toString();
}

// 값이 있는 인터폴레이션(${...})을 포함하는지 판정하는 공용 헬퍼를 re-export 한다.
export { isInterpolationString };

function toJsonObject(doc: Document): JsonObject {
  const value = doc.toJS();
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as JsonObject;
}
